/**
 * Mobile Mega Menu
 * Slide-in drawer with a root list and any number of nested sliding sub-panels —
 * each panel opened while another is already open stacks on top of it, and the
 * back button pops one level at a time. Opened/closed via the
 * `mobile-mega-menu:toggle` / `:close` events (dispatched by the trigger snippet)
 * or the close/overlay controls. Scroll lock, focus management, Escape handling
 * and a focus trap are included.
 */

(function () {
// Read the live drawer transition from --animation-speed-slow so focus timing
// stays in sync if a merchant customises it. Token is in seconds (e.g. "0.2s");
// fall back to 200ms. Under prefers-reduced-motion the transition is ~instant,
// so focus moves immediately (see focusDelay()).
const TRANSITION_MS =
  parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--animation-speed-slow')) * 1000 || 200;
// Brief delay to let the drawer finish rendering before moving focus
const FOCUS_DELAY_MS = 100;

// Honour reduced-motion: when transitions are effectively off, don't sit on a
// timer before delivering focus.
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const focusDelay = (ms) => (prefersReducedMotion.matches ? 0 : ms);

class MobileMegaMenu {
  /**
   * Create a new MobileMegaMenu and initialise the drawer.
   */
  constructor() {
    this.menu = document.querySelector('[data-mm]');
    this.overlay = document.querySelector('[data-mm-overlay]');
    if (!this.menu || !this.overlay) return;

    this.root = this.menu.querySelector('[data-mm-root]');
    this.backButton = this.menu.querySelector('[data-mm-back]');
    this.closeButton = this.menu.querySelector('[data-mm-close]');
    this.titleEl = this.menu.querySelector('[data-mm-title]');
    this.triggers = [...document.querySelectorAll('[data-mobile-mega-menu-trigger]')];

    /** @type {{ panel: HTMLElement, opener: HTMLElement | null }[]} */
    this.panelStack = [];
    /**
     * The one drawer-footer (sign-in/localization/help/copyright) rendered by the
     * currently-open top-level block, and the panel it's natively rendered inside —
     * moved deeper alongside the panel stack instead of being duplicated per level.
     * @type {{ element: HTMLElement, originalParent: HTMLElement } | null}
     */
    this.footerHome = null;
    this.lastFocused = null;
    this.previousScrollY = 0;
    this.focusTrapHandler = null;
    this._escHandler = null;
    this._toggleHandler = null;
    this._closeHandler = null;
    this._triggerClickHandler = null;
    this._closeButtonHandler = null;
    this._overlayHandler = null;
    this._backButtonHandler = null;
    this._rootClickHandler = null;

    this.init();
  }

  /**
   * Initialise events and move sub-panels out of the inert root.
   * @returns {void}
   */
  init() {
    // Panels live inside [data-mm-root] in the DOM (Shopify blocks render there),
    // but root.inert = true cascades and freezes them. Move panels to be direct
    // children of the <aside> so root.inert never reaches them.
    this.menu.querySelectorAll('[data-mm-panel]').forEach((panel) => {
      this.menu.appendChild(panel);

      // Wrap promo tiles in a bottom container (rails stay at top where blocks
      // rendered). Inserted before the shared drawer-footer (sign-in/about/help/
      // localization/copyright) so promos sit above it, not below.
      const promos = [...panel.querySelectorAll(':scope > .mega-menu__promo')];
      if (promos.length > 0) {
        const wrapper = document.createElement('div');
        wrapper.className = 'mobile-mega-menu__promos';
        const drawerFooter = panel.querySelector(':scope > [data-mm-drawer-footer]');
        panel.insertBefore(wrapper, drawerFooter);
        promos.forEach((p) => wrapper.appendChild(p));
      }
    });

    this._triggerClickHandler = () => this.toggle();
    for (const trigger of this.triggers) {
      trigger.addEventListener('click', this._triggerClickHandler);
    }

    this._closeButtonHandler = () => this.close();
    this._overlayHandler = () => this.close();
    this._backButtonHandler = () => this.closePanel();
    this.closeButton?.addEventListener('click', this._closeButtonHandler);
    this.overlay.addEventListener('click', this._overlayHandler);
    this.backButton?.addEventListener('click', this._backButtonHandler);

    this._toggleHandler = () => this.toggle();
    this._closeHandler = () => this.close();
    this._escHandler = (e) => {
      if (e.key !== 'Escape' || !this.isOpen()) return;
      // Let an open nested popover (e.g. the country/localization dropdown) close
      // itself first — it listens on `keyup`, which fires after this `keydown`, so
      // bailing here keeps Escape scoped to the topmost open widget.
      if (this.menu.querySelector('dropdown-localization-component .localization-wrapper:not([hidden])')) {
        return;
      }
      if (this.panelStack.length > 0) {
        this.closePanel();
      } else {
        this.close();
      }
    };
    document.addEventListener('mobile-mega-menu:toggle', this._toggleHandler);
    document.addEventListener('mobile-mega-menu:close', this._closeHandler);

    // Delegated on the whole drawer, not just the root list, so a drill-down row
    // inside an already-open panel (a deeper menu level) also opens its panel.
    this._rootClickHandler = (e) => {
      const opener = e.target.closest('[data-mm-open]');
      if (opener) this.openPanel(opener.getAttribute('data-mm-open'), opener);
    };
    this.menu.addEventListener('click', this._rootClickHandler);

    document.addEventListener('keydown', this._escHandler);

    this.setupEditorEvents();
  }

  /**
   * Register Shopify theme editor events so the drawer stays functional after section re-renders.
   * @returns {void}
   */
  setupEditorEvents() {
    document.addEventListener('shopify:section:load', () => {
      window.__mobileMegaMenu?.destroy();
      window.__mobileMegaMenu = new MobileMegaMenu();
    }, { once: true });
  }

  /**
   * Remove all document-level listeners so this instance can be safely replaced.
   * @returns {void}
   */
  destroy() {
    this.removeFocusTrap();
    if (this._escHandler) {
      document.removeEventListener('keydown', this._escHandler);
      this._escHandler = null;
    }
    if (this._toggleHandler) {
      document.removeEventListener('mobile-mega-menu:toggle', this._toggleHandler);
      this._toggleHandler = null;
    }
    if (this._closeHandler) {
      document.removeEventListener('mobile-mega-menu:close', this._closeHandler);
      this._closeHandler = null;
    }
    if (this._triggerClickHandler) {
      for (const trigger of this.triggers) {
        trigger.removeEventListener('click', this._triggerClickHandler);
      }
      this._triggerClickHandler = null;
    }
    if (this._closeButtonHandler) {
      this.closeButton?.removeEventListener('click', this._closeButtonHandler);
      this._closeButtonHandler = null;
    }
    if (this._overlayHandler) {
      this.overlay?.removeEventListener('click', this._overlayHandler);
      this._overlayHandler = null;
    }
    if (this._backButtonHandler) {
      this.backButton?.removeEventListener('click', this._backButtonHandler);
      this._backButtonHandler = null;
    }
    if (this._rootClickHandler) {
      this.menu?.removeEventListener('click', this._rootClickHandler);
      this._rootClickHandler = null;
    }
  }

  /** @returns {boolean} True if the drawer is currently open. */
  isOpen() {
    return this.menu.classList.contains('is-active');
  }

  /**
   * Toggle the drawer open or closed.
   * @returns {void}
   */
  toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open the drawer, lock scroll, and trap focus.
   * @returns {void}
   */
  open() {
    this.lastFocused = document.activeElement;
    this.previousScrollY = window.scrollY;

    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.previousScrollY}px`;

    this.menu.classList.add('is-active');
    this.overlay.classList.add('is-active');
    this.menu.removeAttribute('aria-hidden');
    this.menu.inert = false;

    for (const trigger of this.triggers) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    this.setupFocusTrap();
    setTimeout(() => this.closeButton?.focus(), focusDelay(FOCUS_DELAY_MS));
  }

  /**
   * Close the drawer, restore scroll position, and return focus.
   * @returns {void}
   */
  close() {
    this.closeAllPanels();

    this.menu.classList.remove('is-active');
    this.overlay.classList.remove('is-active');
    this.menu.setAttribute('aria-hidden', 'true');
    this.menu.inert = true;

    for (const trigger of this.triggers) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.previousScrollY, behavior: 'instant' });

    this.removeFocusTrap();

    if (this.lastFocused) {
      this.lastFocused.focus();
      this.lastFocused = null;
    }
  }

  /**
   * The currently visible/interactive container: the deepest open panel, or the
   * root list when no panel is open yet.
   * @returns {HTMLElement | null}
   */
  currentContainer() {
    return this.panelStack.length > 0 ? this.panelStack[this.panelStack.length - 1].panel : this.root;
  }

  /**
   * Move the shared drawer-footer (see this.footerHome) to be the last child of the
   * given container, if it isn't already there. No-op if there's no footer to move.
   * @param {HTMLElement | null} container
   * @returns {void}
   */
  relocateFooter(container) {
    if (!this.footerHome || !container) return;
    if (this.footerHome.element.parentElement !== container) {
      container.appendChild(this.footerHome.element);
    }
  }

  /**
   * Slide in a sub-panel by ID, stacking it on top of whatever panel (or the
   * root list) is currently open.
   * @param {string} panelId - The value of [data-mm-panel] on the target panel.
   * @param {HTMLElement} [opener] - The element that triggered the open.
   * @returns {void}
   */
  openPanel(panelId, opener) {
    const panel = this.menu.querySelector(`[data-mm-panel="${panelId}"]`);
    if (!panel) return;

    const previousContainer = this.currentContainer();
    if (previousContainer) previousContainer.inert = true;

    // Only a top-level block's own panel renders a drawer-footer; every panel below
    // it shares that single instance. Grab it the moment we drill past that first
    // level (previousContainer is still that top-level panel here), then carry it
    // deeper on every subsequent open via relocateFooter().
    if (this.panelStack.length === 1 && !this.footerHome) {
      const footerElement = previousContainer?.querySelector(':scope > [data-mm-drawer-footer]');
      if (footerElement) {
        this.footerHome = { element: footerElement, originalParent: previousContainer };
      }
    }

    panel.classList.add('is-active');
    panel.inert = false;
    panel.removeAttribute('aria-hidden');
    opener?.setAttribute('aria-expanded', 'true');

    this.panelStack.push({ panel, opener: opener || null });
    this.relocateFooter(panel);

    if (this.titleEl) {
      this.titleEl.textContent = panel.getAttribute('data-mm-panel-title') || '';
    }
    if (this.backButton) this.backButton.hidden = false;

    setTimeout(() => {
      const first = this.getFocusableElements()[0];
      first?.focus();
    }, focusDelay(TRANSITION_MS));
  }

  /**
   * Slide out the topmost open sub-panel, revealing whatever panel (or the root
   * list) was open beneath it.
   * @param {{ restoreFocus?: boolean }} [options]
   * @returns {void}
   */
  closePanel({ restoreFocus = true } = {}) {
    const entry = this.panelStack.pop();
    if (!entry) return;

    const { panel, opener } = entry;
    panel.classList.remove('is-active');
    panel.inert = true;
    panel.setAttribute('aria-hidden', 'true');

    const revealedContainer = this.currentContainer();
    if (revealedContainer) revealedContainer.inert = false;

    if (this.footerHome) {
      if (this.panelStack.length === 0) {
        // Backed all the way out of the top-level block — return its footer so it's
        // there again, natively, next time this block's own panel is opened.
        this.relocateFooter(this.footerHome.originalParent);
        this.footerHome = null;
      } else {
        this.relocateFooter(revealedContainer);
      }
    }

    const revealedPanel = this.panelStack[this.panelStack.length - 1]?.panel;
    if (this.titleEl) {
      this.titleEl.textContent = revealedPanel?.getAttribute('data-mm-panel-title') || '';
    }
    if (this.backButton) this.backButton.hidden = this.panelStack.length === 0;

    opener?.setAttribute('aria-expanded', 'false');
    if (restoreFocus && opener) {
      setTimeout(() => opener.focus(), focusDelay(TRANSITION_MS));
    }
  }

  /**
   * Slide out every open sub-panel, back down to the root list. Used when the
   * whole drawer closes.
   * @returns {void}
   */
  closeAllPanels() {
    while (this.panelStack.length > 0) {
      this.closePanel({ restoreFocus: false });
    }
  }

  /**
   * Return focusable elements within the whole drawer (not just the active panel), so the
   * header's Back/Close buttons stay reachable by keyboard while a sub-panel is open.
   * @returns {HTMLElement[]}
   */
  getFocusableElements() {
    const selectors =
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    // Always trap against the whole drawer, not just the active panel — the header's
    // Back/Close buttons live outside the panel and must stay reachable by keyboard.
    // [inert] filtering below already excludes the root's other, hidden panels.
    return Array.from(this.menu.querySelectorAll(selectors)).filter((el) => {
      if (el.closest('[inert]')) return false;
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetParent !== null;
    });
  }

  /**
   * Attach a Tab-key handler to trap focus within the open drawer.
   * @returns {void}
   */
  setupFocusTrap() {
    this.focusTrapHandler = (e) => {
      if (e.key !== 'Tab' || !this.isOpen()) return;
      const focusable = this.getFocusableElements();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', this.focusTrapHandler);
  }

  /**
   * Remove the active focus trap keydown handler.
   * @returns {void}
   */
  removeFocusTrap() {
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }
}

if (!window.__mobileMegaMenu) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.__mobileMegaMenu = new MobileMegaMenu();
    });
  } else {
    window.__mobileMegaMenu = new MobileMegaMenu();
  }
}
})();
